import * as React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { Check, Close, EditOutlined } from '@mui/icons-material';
import { useAppTranslation } from '../../services/i18n';

interface Props {
  /** the workplace the half-finished form belongs to */
  name: string;
  /** the form still has a complaint of its own, so there is nothing to save yet */
  canSave: boolean;
  isSaving: boolean;
  onDiscard: () => void;
  onKeepEditing: () => void;
  onSave: () => void;
}

/**
 * Stands between a half-finished form and whatever wants to take it away -- Cancel,
 * Escape, or an action reached from the row menu behind it. It offers all three ways
 * out rather than only asking "are you sure", so saving does not mean going back and
 * pressing the panel's own button.
 */
export const WorkplaceUnsavedDialog: React.FC<Props> = ({
  name,
  canSave,
  isSaving,
  onDiscard,
  onKeepEditing,
  onSave,
}) => {
  const t = useAppTranslation();

  return (
    <Dialog open={true} onClose={onKeepEditing} maxWidth={'sm'} fullWidth={true}>
      <DialogTitle className={'workplace-unsaved__title'}>
        <span>{t('workplaces.unsaved.title')}</span>
        <IconButton
          size={'small'}
          color={'inherit'}
          onClick={onKeepEditing}
          aria-label={t('form.close')}
        >
          <Close fontSize={'small'} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <p>{t('workplaces.unsaved.lead', { name })}</p>
        <p className={'workplace-unsaved__question'}>
          {canSave ? t('workplaces.unsaved.question') : t('workplaces.unsaved.invalid')}
        </p>
      </DialogContent>
      <DialogActions>
        <Button
          variant={'outlined'}
          color={'inherit'}
          startIcon={<Close />}
          onClick={onDiscard}
          disabled={isSaving}
        >
          {t('workplaces.unsaved.discard')}
        </Button>
        <Button
          variant={'outlined'}
          color={'inherit'}
          startIcon={<EditOutlined />}
          onClick={onKeepEditing}
          disabled={isSaving}
        >
          {t('workplaces.unsaved.keepEditing')}
        </Button>
        <Button
          variant={'contained'}
          color={'primary'}
          startIcon={<Check />}
          onClick={onSave}
          disabled={!canSave || isSaving}
        >
          {t('workplaces.unsaved.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
